import React, { useEffect, useState } from "react";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import InstagramFeed from "../components/InstagramFeed";

const ArtistPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    api
      .get(`/artists/${id}`)
      .then((r) => setArtist(r.data))
      .catch(() => setError("Artist not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#121212]">
        <div className="w-10 h-10 border-2 border-gold/20 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !artist) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#121212] gap-4">
        <p className="font-display text-4xl text-white/30">Artist Not Found</p>
        <button onClick={() => navigate("/portal")} className="btn-outline-gold px-6 py-3 text-xs rounded-sm">
          Back to Roster
        </button>
      </div>
    );
  }

  // No more video; we use Instagram feed below

  return (
    <div className="min-h-screen bg-[#121212] text-white" data-testid="artist-page">
      {/* Hero */}
      <div className="relative h-[70vh] min-h-[500px] overflow-hidden">
        <img
          src={artist.image}
          alt={artist.name}
          className="w-full h-full object-cover"
          style={{ objectPosition: "center 22%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#121212]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/30" />

        <button
          onClick={() => navigate(isAdmin ? "/admin" : "/portal")}
          className="absolute top-24 left-6 flex items-center gap-2 text-white/60 hover:text-gold transition-colors font-body text-xs tracking-wider uppercase z-10"
          data-testid="artist-back"
        >
          <ArrowLeft size={14} />
          <span>{isAdmin ? "Dashboard" : "All Artists"}</span>
        </button>

        {isAdmin && (
          <button
            onClick={() => navigate(`/admin/artists/${artist.id}/edit`)}
            className="absolute top-24 right-6 btn-gold px-5 py-2 text-xs rounded-sm z-10"
            data-testid="artist-edit"
          >
            Edit Profile
          </button>
        )}

        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-16 pb-12">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap gap-2 mb-4">
              {artist.specialties.map((s) => (
                <span key={s} className="specialty-tag">{s}</span>
              ))}
            </div>
            <h1 className="font-display font-light text-6xl md:text-8xl text-white leading-none mb-2">{artist.name}</h1>
            <p className="font-body text-gold text-sm tracking-[0.3em] uppercase">{artist.title}</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-16 py-16">
        <div className="grid md:grid-cols-3 gap-14">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-gold/60" />
              <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Biography</span>
            </div>
            <div className="mb-10">
              {(artist.bio || "")
                .split(/\n\s*\n|\n/)
                .map((s) => s.trim())
                .filter(Boolean)
                .map((para, i) => (
                  <p
                    key={i}
                    className="font-body text-white/60 text-sm md:text-[0.95rem] mb-5 last:mb-0"
                    style={{ textAlign: "justify", textJustify: "inter-word", hyphens: "auto", lineHeight: 1.85 }}
                  >
                    {para}
                  </p>
                ))}
            </div>

            <div className="mb-10">
              <InstagramFeed
                profileUrl={artist.instagramUrl}
                posts={artist.instagramPosts}
                artistName={artist.name}
              />
            </div>

            {artist.galleryImages && artist.galleryImages.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-px w-8 bg-gold/60" />
                  <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">Gallery</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {artist.galleryImages.map((img, i) => (
                    <div key={i} className="relative overflow-hidden rounded-sm group bg-black/40 border border-gold/10">
                      <img
                        src={img}
                        alt={`${artist.name} ${i + 1}`}
                        className="block w-full h-auto object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-8">
            <div className="glass rounded-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-5 bg-gold/60" />
                <span className="font-body text-gold text-[0.6rem] font-semibold tracking-[0.3em] uppercase">Career Stats</span>
              </div>
              <div className="space-y-5">
                {artist.stats.map((s) => (
                  <div key={s.label} className="flex justify-between items-center">
                    <span className="font-body text-white/40 text-xs tracking-wide">{s.label}</span>
                    <span className="stat-number text-2xl font-bold">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass rounded-sm p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-5 bg-gold/60" />
                <span className="font-body text-gold text-[0.6rem] font-semibold tracking-[0.3em] uppercase">Specialties</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {artist.specialties.map((s) => (
                  <span key={s} className="specialty-tag">{s}</span>
                ))}
              </div>
            </div>

            <div className="border border-gold/30 rounded-sm p-6 text-center">
              <p className="font-body text-white/40 text-xs tracking-wider mb-4 uppercase">Interested in booking?</p>
              <h3 className="font-display text-xl text-white mb-4">Work with {artist.name.split(" ")[0]}</h3>
              <button
                onClick={() => navigate(`/contact?artistId=${artist.id}`)}
                className="btn-gold w-full py-3 text-xs rounded-sm flex items-center justify-center gap-2"
                data-testid="artist-request-booking"
              >
                <span>Request Booking</span>
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistPage;
